// Auto-translated menu item names.
//
// A small reactive store mapping an item's *source* name (item_name as it
// comes from the POS / API) to its translation in the ACTIVE locale. The map
// is filled by calling the backend `order/translate-menu` endpoint, which in
// turn caches results to a per-locale JSON file so each string is only ever
// translated once.
//
// Components read names through `translatedName()` so they re-render
// automatically whenever the active locale or the map changes. "en" is the
// source language, so it returns originals with no API call.
import { reactive } from "vue";
import OrderService from "@/services/order.service";
import i18n, { SUPPORTED_LOCALES } from "@/i18n";

const state = reactive({
  locale: "en",
  map: {}, // source name -> translated name (active locale only)
});

// M1044: translations fetched ahead for every locale (locale -> source ->
// translated), so switching language renders at once instead of waiting for
// the backend (seconds when names are not in its cache yet).
const prefetched = {};
// Names already on their way per locale: the menu re-syncs several times while
// it loads, and must not ask for the same names again meanwhile.
const pending = {};

function remember(locale, translations) {
  prefetched[locale] = Object.assign(prefetched[locale] || {}, translations);
}

function uniqueNames(names) {
  return [
    ...new Set(
      (names || []).filter((n) => typeof n === "string" && n.trim() !== "")
    ),
  ];
}

// Translation for a source name in the ACTIVE locale, falling back to the
// source string itself (covers en, in-flight fetches, and any failure).
export function translatedName(source) {
  if (!source || state.locale === "en") return source;
  return state.map[source] || source;
}

export function activeMenuLocale() {
  return state.locale;
}

// Fetch translations for `names` in `locale` and merge them into the map.
// Switching to "en" just clears the map so originals render. Safe to call
// repeatedly — already-cached names are served from the backend JSON cache.
export async function loadMenuTranslations(names, locale) {
  if (locale === "en") {
    state.locale = "en";
    state.map = {};
    return;
  }

  // A new locale must not keep the previous locale's names (KSK096: the kiosk
  // header can switch language mid-session).
  if (state.locale !== locale) state.map = { ...(prefetched[locale] || {}) };
  state.locale = locale;

  const known = prefetched[locale] || {};
  const unique = uniqueNames(names).filter((n) => !(n in known));
  if (unique.length === 0) return;

  try {
    const res = await OrderService.translateMenu({ locale, names: unique });
    if (res?.data?.result?.isSuccess) {
      const incoming = res.data.data?.translations || {};
      remember(locale, incoming);
      // Guard against a stale response landing after the user switched again.
      if (state.locale === locale) {
        Object.assign(state.map, incoming);
      }
    }
  } catch (e) {
    // Graceful: leave originals showing.
  }
}

// M1044: translate `names` into every language in the background as soon as
// the menu opens, so the language button switches with no wait. Names already
// fetched for a locale are not asked again.
export function prefetchMenuTranslations(names) {
  const unique = uniqueNames(names);
  if (unique.length === 0) return;

  SUPPORTED_LOCALES.filter((l) => l !== "en").forEach(async (locale) => {
    const known = prefetched[locale] || {};
    const inFlight = pending[locale] || (pending[locale] = new Set());
    const missing = unique.filter((n) => !(n in known) && !inFlight.has(n));
    if (missing.length === 0) return;
    missing.forEach((n) => inFlight.add(n));
    try {
      const res = await OrderService.translateMenu({ locale, names: missing });
      if (res?.data?.result?.isSuccess) {
        const incoming = res.data.data?.translations || {};
        remember(locale, incoming);
        if (state.locale === locale) Object.assign(state.map, incoming);
      }
    } catch (e) {
      // Graceful: the switch falls back to fetching on demand.
    } finally {
      missing.forEach((n) => inFlight.delete(n));
    }
  });
}

// KSK123: the kiosk translates its whole current menuset into every language
// from Landing, then every 15 minutes picks up items added since. One timer
// for the page's lifetime: it keeps running while customers order, and calling
// again (each Landing visit) only swaps in the newest loader.
const KIOSK_WARM_INTERVAL_MS = 15 * 60 * 1000;
let kioskWarmLoader = null;
let kioskWarmTimer = null;

async function runKioskMenuWarm() {
  try {
    prefetchMenuTranslations(await kioskWarmLoader());
  } catch (e) {
    // Graceful: the next run (or the menu itself) tries again.
  }
}

export function startKioskMenuWarm(loadNames) {
  kioskWarmLoader = loadNames;
  runKioskMenuWarm();
  if (!kioskWarmTimer) {
    kioskWarmTimer = setInterval(runKioskMenuWarm, KIOSK_WARM_INTERVAL_MS);
  }
}

// Convenience wrapper: translate `names` into the CURRENTLY active i18n
// locale. Used by the cart / payment-success / order-history / item-detail
// screens so their item names stay in sync with the language chosen on the
// menu — even on a direct visit/refresh where the menu never loaded.
export function syncMenuTranslations(names) {
  return loadMenuTranslations(names, i18n.global.locale.value);
}

// Recursively pull every displayed name out of a menu (categories → groups →
// sub-groups → items) or an order-line array, so one call can translate
// everything a screen will show. Tolerates the various shapes used across the
// app (item_name, second_name, name, itemName; nested `item`;
// `choice_selected.items[].item_detail`; `modifier_group[].items[]`).
const CHILD_LISTS = [
  "items",
  "groups",
  "sub_groups",
  "sub_group_items",
  "items_ordering_data",
];

export function collectItemNames(items) {
  const out = [];
  const push = (v) => {
    if (typeof v === "string" && v.trim() !== "") out.push(v);
  };
  const walk = (node) => {
    if (!node || typeof node !== "object") return;
    push(node.item_name);
    push(node.second_name);
    push(node.name);
    push(node.itemName);
    if (node.item && typeof node.item === "object") walk(node.item);
    const cs = node.choice_selected;
    if (cs && Array.isArray(cs.items)) {
      cs.items.forEach((ci) => ci && ci.item_detail && walk(ci.item_detail));
    }
    if (Array.isArray(node.modifier_group)) {
      node.modifier_group.forEach((g) => (g.items || []).forEach(walk));
    }
    CHILD_LISTS.forEach((key) => {
      if (Array.isArray(node[key])) node[key].forEach(walk);
    });
  };
  (items || []).forEach(walk);
  return out;
}

// KSK054: every name anywhere inside an object tree (item detail with
// choices → item_detail → modifier_group → item_in_mog, cart lines, …). The
// kiosk shapes nest deeper and under more keys than collectItemNames walks, and
// modifier GROUP names need translating too, so this just takes them all.
const NAME_KEYS = ["item_name", "second_name", "name", "itemName"];

export function collectNamesDeep(root) {
  const out = [];
  const walk = (node) => {
    if (Array.isArray(node)) {
      node.forEach(walk);
      return;
    }
    if (!node || typeof node !== "object") return;
    Object.keys(node).forEach((key) => {
      const v = node[key];
      if (typeof v === "string") {
        if (NAME_KEYS.includes(key) && v.trim() !== "") out.push(v);
      } else if (v && typeof v === "object") {
        walk(v);
      }
    });
  };
  walk(root);
  return out;
}
